import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import api from '../services/api';
import { Workflow, Group } from '../types';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import WorkflowBuilder from '../components/WorkflowBuilder';
import WorkflowBPMNViewer from '../components/WorkflowBPMNViewer';
import CustomFieldsManager from '../components/CustomFieldsManager';
import SubtaskTypesManager from '../components/SubtaskTypesManager';
import AlertModal from '../components/AlertModal';
import ActionIconButton from '../components/ActionIconButton';
import { buildFriendlyApiError } from '../utils/apiError';

const Workflows: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { currentArea } = useArea();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isViewModalMaximized, setIsViewModalMaximized] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);
  const [showSubtaskTypesModal, setShowSubtaskTypesModal] = useState(false);
  const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [importWorkflowName, setImportWorkflowName] = useState('');
  const [importWorkflowDescription, setImportWorkflowDescription] = useState('');
  const [importWorkflowData, setImportWorkflowData] = useState<any>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState<{title: string; message: string; variant: 'success' | 'danger' | 'warning' | 'info'; details?: string}>({
    title: '',
    message: '',
    variant: 'info'
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [workflowToToggle, setWorkflowToToggle] = useState<{id: number; name: string; is_active: boolean} | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<{id: number; name: string} | null>(null);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [workflowToPublish, setWorkflowToPublish] = useState<Workflow | null>(null);
  const [showNewVersionConfirmModal, setShowNewVersionConfirmModal] = useState(false);
  const [workflowToNewVersion, setWorkflowToNewVersion] = useState<Workflow | null>(null);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [workflowVersions, setWorkflowVersions] = useState<Workflow[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [publicationFilter, setPublicationFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [sortField, setSortField] = useState<'id' | 'name' | 'version' | 'status' | 'publication' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const filteredAndSortedWorkflows = useMemo(() => {
    const rawSearch = searchTerm.trim();
    const normalizedSearch = rawSearch.toLowerCase();

    const filtered = workflows.filter((workflow) => {
      let matchesSearch = false;
      if (rawSearch.length === 0) {
        matchesSearch = true;
      } else if (
        rawSearch.startsWith('#') &&
        rawSearch.length > 1 &&
        /^\d+$/.test(rawSearch.slice(1))
      ) {
        matchesSearch = workflow.id === parseInt(rawSearch.slice(1), 10);
      } else {
        matchesSearch =
          workflow.name.toLowerCase().includes(normalizedSearch) ||
          (workflow.description || '').toLowerCase().includes(normalizedSearch);
      }

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? workflow.is_active : !workflow.is_active);

      const matchesPublication =
        publicationFilter === 'all' ||
        (publicationFilter === 'published' ? !!workflow.is_published : !workflow.is_published);

      return matchesSearch && matchesStatus && matchesPublication;
    });

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'id') {
        comparison = a.id - b.id;
      } else if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
      } else if (sortField === 'version') {
        comparison = (a.version || 1) - (b.version || 1);
      } else if (sortField === 'status') {
        comparison = Number(a.is_active) - Number(b.is_active);
      } else if (sortField === 'publication') {
        comparison = Number(!!a.is_published) - Number(!!b.is_published);
      } else if (sortField === 'created_at') {
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [workflows, searchTerm, statusFilter, publicationFilter, sortField, sortDirection]);

  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'analista') {
      fetchWorkflows();
    }
  }, [currentUser, currentArea]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      // Buscar todos os workflows (incluindo não publicados) para a tela de gerenciamento
      // Filtrar por área atual se estiver definida
      const params: any = {
        only_valid: false,
        skip: 0,
        limit: 1000  // Buscar muitos para garantir que todos apareçam
      };
      
      // Adicionar filtro por área se estiver definida
      if (currentArea) {
        params.area_type = currentArea;
      }
      
      const response = await api.get('/api/workflows', { params });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWorkflow = (workflow: Workflow) => {
    setWorkflowToToggle({
      id: workflow.id,
      name: workflow.name,
      is_active: workflow.is_active
    });
    setShowConfirmModal(true);
  };

  const handleDeleteWorkflow = (workflow: Workflow) => {
    setWorkflowToDelete({
      id: workflow.id,
      name: workflow.name
    });
    setShowDeleteModal(true);
  };

  const handlePublish = (workflow: Workflow) => {
    setWorkflowToPublish(workflow);
    setShowPublishConfirmModal(true);
  };

  const handleConfirmPublish = async () => {
    if (!workflowToPublish) return;

    try {
      await api.post(`/api/workflows/${workflowToPublish.id}/publish`);
      setShowPublishConfirmModal(false);
      setWorkflowToPublish(null);
      setAlertModalData({
        title: 'Sucesso',
        message: `Fluxo "${workflowToPublish.name}" foi publicado com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
      fetchWorkflows();
    } catch (error: any) {
      console.error('Erro ao publicar fluxo:', error);
      setShowPublishConfirmModal(false);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao publicar fluxo.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setWorkflowToPublish(null);
    }
  };

  const handleNewVersion = (workflow: Workflow) => {
    setWorkflowToNewVersion(workflow);
    setShowNewVersionConfirmModal(true);
  };

  const handleViewVersions = async (workflow: Workflow, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingWorkflow(workflow);
    setShowVersionsModal(true);
    setLoadingVersions(true);
    setWorkflowVersions([]);
    
    try {
      const url = `/api/workflows/${workflow.id}/versions`;
      const response = await api.get(url);
      setWorkflowVersions(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar versões:', error);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao carregar versões do fluxo.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setWorkflowVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleConfirmNewVersion = async () => {
    if (!workflowToNewVersion) return;

    try {
      const response = await api.post(`/api/workflows/${workflowToNewVersion.id}/new-version`);
      setShowNewVersionConfirmModal(false);
      setAlertModalData({
        title: 'Sucesso',
        message: `Nova versão (v${response.data.version}) do fluxo "${workflowToNewVersion.name}" foi criada com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
      fetchWorkflows();
      
      // Opcionalmente, abrir o modal de edição da nova versão
      setTimeout(() => {
        const newWorkflow: Workflow = response.data;
        setEditingWorkflow(newWorkflow);
        setShowModal(true);
      }, 500);
      setWorkflowToNewVersion(null);
    } catch (error: any) {
      console.error('Erro ao criar nova versão:', error);
      setShowNewVersionConfirmModal(false);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao criar nova versão.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setWorkflowToNewVersion(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!workflowToDelete) return;

    try {
      await api.delete(`/api/workflows/${workflowToDelete.id}/permanent`);
      setShowDeleteModal(false);
      setWorkflowToDelete(null);
      setAlertModalData({
        title: 'Sucesso',
        message: `Fluxo "${workflowToDelete.name}" foi excluído definitivamente!`,
        variant: 'success'
      });
      setShowAlertModal(true);
        fetchWorkflows();
    } catch (error: any) {
      console.error('Erro ao excluir fluxo:', error);
      const friendly = buildFriendlyApiError(error, {
        defaultMessage: 'Erro ao excluir fluxo definitivamente.',
        endpointHints: ['/workflows', '/permanent'],
      });
      setShowDeleteModal(false);
      setAlertModalData({
        title: 'Erro',
        message: friendly.message,
        variant: 'danger',
        details: friendly.details,
      });
      setShowAlertModal(true);
      setWorkflowToDelete(null);
    }
  };

  const handleConfirmToggle = async () => {
    if (!workflowToToggle) return;

    try {
      // Usar PUT para atualizar o status (ativar/desativar)
      await api.put(`/api/workflows/${workflowToToggle.id}`, {
        is_active: !workflowToToggle.is_active
      });
      setShowConfirmModal(false);
      setWorkflowToToggle(null);
      setAlertModalData({
        title: 'Sucesso',
        message: `Fluxo "${workflowToToggle.name}" foi ${workflowToToggle.is_active ? 'desativado' : 'ativado'} com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
      fetchWorkflows();
    } catch (error: any) {
      console.error('Erro ao alterar status do fluxo:', error);
      setShowConfirmModal(false);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao alterar status do fluxo.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setWorkflowToToggle(null);
    }
  };

  const handleExport = async (workflow: Workflow) => {
    try {
      // Buscar dados completos do workflow incluindo grupos, campos customizados e configs de e-mail
      const [workflowResponse, groupsResponse, customFieldsResponse, emailConfigsResponse] = await Promise.all([
        api.get(`/api/workflows/${workflow.id}`),
        api.get(`/api/workflows/${workflow.id}/groups`),
        api.get(`/api/workflows/${workflow.id}/custom-fields`),
        api.get(`/api/workflows/${workflow.id}/email-configs`)
      ]);

      // Buscar tipos de subtarefas separadamente para não quebrar a exportação se falhar
      let subtaskTypes: any[] = [];
      try {
        const subtaskTypesResponse = await api.get(`/api/workflows/${workflow.id}/subtask-types`);
        subtaskTypes = subtaskTypesResponse.data || [];
      } catch (error: any) {
        console.warn('Erro ao buscar subtask types (continuando sem eles):', error);
        subtaskTypes = [];
      }

      const workflowData = workflowResponse.data;
      const workflowGroups = groupsResponse.data.map((wg: any) => wg.group || {
        id: wg.group_id,
        name: wg.group?.name || '',
        description: wg.group?.description || ''
      });
      const customFields = customFieldsResponse.data;
      const emailConfigs = emailConfigsResponse.data || [];

      // Buscar configurações de step para cada campo customizado
      const customFieldsWithConfigs = await Promise.all(
        customFields.map(async (field: any) => {
          try {
            const stepConfigsResponse = await api.get(
              `/api/workflows/${workflow.id}/custom-fields/${field.id}/step-configs`
            );
            return {
              ...field,
              step_configs: stepConfigsResponse.data
            };
      } catch (error) {
            return {
              ...field,
              step_configs: []
            };
          }
        })
      );

      // Parse dos steps
      let steps: any[] = [];
      if (workflowData.steps) {
        try {
          const stepsData = JSON.parse(workflowData.steps);
          if (Array.isArray(stepsData)) {
            steps = stepsData;
          } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
            steps = stepsData.steps;
          }
        } catch (e) {
          // Se não for JSON válido, tratar como string simples
          steps = workflowData.steps.split(',').map((s: string) => ({ 
            name: s.trim(), 
            status: 'em_andamento' 
          }));
        }
      }

      // Buscar todos os workflows para mapear IDs de subtarefas para nomes
      let allWorkflows: Workflow[] = [];
      try {
        const allWorkflowsResponse = await api.get(`/api/workflows`);
        allWorkflows = allWorkflowsResponse.data || [];
      } catch (error) {
        console.warn('Erro ao buscar workflows para mapear nomes:', error);
      }

      // Construir estrutura JSON de exportação
      const exportData = {
        schema_version: 1,
        exported_at: new Date().toISOString(),
        workflow: {
          name: workflowData.name,
          description: workflowData.description || null,
          is_active: workflowData.is_active,
          area_type: workflowData.area_type || currentArea
        },
        steps: steps.map((step: any) => ({
          name: typeof step === 'string' ? step : (step.name || ''),
          status: typeof step === 'string' ? 'em_andamento' : (step.status || 'em_andamento'),
          description: typeof step === 'string' ? null : (step.description || null),
          previous_steps: typeof step === 'string' ? [] : (step.previous_steps || []),
          allow_anytime_access: typeof step === 'string' ? false : !!step.allow_anytime_access,
        })),
        groups: workflowGroups.map((group: Group) => ({
          name: group.name,
          description: group.description || null
        })),
        custom_fields: customFieldsWithConfigs.map((field: any) => {
          // Encontrar nome do campo condicional e de validação
          const conditionalField = field.conditional_field_id
            ? customFields.find((f: any) => f.id === field.conditional_field_id)
            : null;
          const validationField = field.validation_field_id
            ? customFields.find((f: any) => f.id === field.validation_field_id)
            : null;

          return {
            name: field.name,
            label: field.label,
            description: field.description || null,
            field_type: field.field_type,
            options: field.options || null,
            allow_multiple: field.allow_multiple,
            is_required: field.is_required,
            order: field.order,
            group_name: field.group_name || null,
            conditional_field_name: conditionalField?.name || null,
            conditional_operator: field.conditional_operator || null,
            conditional_value: field.conditional_value || null,
            validation_field_name: validationField?.name || null,
            validation_rules: field.validation_rules || null,
            checkbox_label: field.checkbox_label || null,
            checkbox_text: field.checkbox_text || null,
            checkbox_text_trigger_option: field.checkbox_text_trigger_option || null,
            checkbox_link: field.checkbox_link || null,
            checkbox_link_text: field.checkbox_link_text || null,
            document_type_id: field.document_type_id || null,
            step_configs: (field.step_configs || []).map((config: any) => ({
              step_name: config.step_name,
              is_visible: config.is_visible,
              is_required: config.is_required,
              is_blocked: config.is_blocked || false,
              order: config.order
            }))
          };
        }),
        subtask_types: subtaskTypes.map((subtaskType: any) => {
          let workflowNameForSubtask = null;
          if (subtaskType.workflow_id_for_subtask) {
            const targetWorkflow = allWorkflows.find((w: Workflow) => w.id === subtaskType.workflow_id_for_subtask);
            workflowNameForSubtask = targetWorkflow?.name || null;
          }
          return {
            name: subtaskType.name,
            description: subtaskType.description || null,
            workflow_name_for_subtask: workflowNameForSubtask,
            default_priority: subtaskType.default_priority || 'p3',
            order: subtaskType.order || 0,
            is_active: subtaskType.is_active !== false
          };
        }),
        email_configs: emailConfigs.map((config: any) => ({
          step_name: config.step_name || null,
          trigger_event: config.trigger_event,
          recipient_type: config.recipient_type,
          enabled: config.enabled !== false,
          subject_template: config.subject_template || null,
          body_template: config.body_template || null
        }))
      };

      // Gerar JSON formatado
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fluxo_${workflowData.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('Erro ao exportar fluxo:', error);
      setAlertModalData({
        title: 'Erro na Exportação',
        message: error.response?.data?.detail || error.message || 'Erro ao exportar fluxo. Verifique o console para mais detalhes.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };

  const parseJSONFile = async (file: File) => {
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      // Validar schema_version
      if (!jsonData.schema_version || jsonData.schema_version !== 1) {
        throw new Error('Versão do schema não suportada. O arquivo deve ser exportado com schema_version 1.');
      }

      // Validar estrutura mínima
      if (!jsonData.workflow || !jsonData.steps) {
        throw new Error('Arquivo JSON inválido: faltam informações do workflow ou etapas.');
      }

      if (!Array.isArray(jsonData.steps) || jsonData.steps.length === 0) {
        throw new Error('Nenhuma etapa encontrada no arquivo JSON.');
      }

      // Normalizar dados para o formato esperado pela importação
      const workflowData: any = {
        name: jsonData.workflow.name || '',
        description: jsonData.workflow.description || '',
        is_active: jsonData.workflow.is_active !== false,
        steps: jsonData.steps || [],
        groups: jsonData.groups || [],
        customFields: jsonData.custom_fields || [],
        stepConfigs: [],
        subtaskTypes: jsonData.subtask_types || [],
        emailConfigs: jsonData.email_configs || []
      };

      // Converter step_configs aninhados em formato plano
      if (workflowData.customFields && Array.isArray(workflowData.customFields)) {
        workflowData.customFields.forEach((field: any) => {
          if (field.step_configs && Array.isArray(field.step_configs)) {
            field.step_configs.forEach((config: any) => {
              workflowData.stepConfigs.push({
                fieldName: field.name,
                step_name: config.step_name,
                is_visible: config.is_visible !== false,
                is_required: config.is_required === true,
                is_blocked: config.is_blocked === true,
                order: config.order || 0
              });
            });
          }
        });
      }

      return workflowData;
    } catch (error: any) {
      console.error('Erro ao parsear JSON:', error);
      if (error instanceof SyntaxError) {
        throw new Error(`Erro ao ler arquivo JSON: arquivo inválido ou corrompido. ${error.message}`);
      }
      throw new Error(`Erro ao ler arquivo JSON: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      const workflowData = await parseJSONFile(file);
      
      // Preencher formulário com dados do JSON (ou deixar vazio para o usuário editar)
      setImportWorkflowName(workflowData.name || '');
      setImportWorkflowDescription(workflowData.description || '');
      setImportWorkflowData(workflowData);
    } catch (error: any) {
      setAlertModalData({
        title: 'Erro na Importação',
        message: error.message || 'Erro ao processar arquivo JSON.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };

  const handleImportConfirm = async () => {
    if (!importWorkflowData) {
      setAlertModalData({
        title: 'Erro',
        message: 'Nenhum arquivo foi carregado.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      return;
    }

    if (!importWorkflowName.trim()) {
      setAlertModalData({
        title: 'Atenção',
        message: 'Por favor, informe um nome para o fluxo.',
        variant: 'warning'
      });
      setShowAlertModal(true);
      return;
    }

    try {
      // Criar o workflow
      const stepsJson = JSON.stringify(importWorkflowData.steps);
      const workflowResponse = await api.post(`/api/workflows`, {
        name: importWorkflowName.trim(),
        description: importWorkflowDescription.trim(),
        steps: stepsJson,
        is_active: importWorkflowData.is_active,
        area_type: currentArea // Incluir area_type na importação
      });

      const newWorkflowId = workflowResponse.data.id;

      // Associar grupos (buscar por nome)
      if (importWorkflowData.groups.length > 0) {
        const allGroupsResponse = await api.get(`/api/groups`, {
          params: { limit: 1000 },
        });
        const allGroups = allGroupsResponse.data;

        for (const groupData of importWorkflowData.groups) {
          const group = allGroups.find((g: Group) => g.name === groupData.name);
          if (group) {
            try {
              await api.post(`/api/workflows/${newWorkflowId}/groups`, {
                group_id: group.id
              });
            } catch (error: any) {
              console.warn(`Grupo "${groupData.name}" não pôde ser associado:`, error.response?.data?.detail || error.message);
            }
          } else {
            console.warn(`Grupo "${groupData.name}" não encontrado no sistema.`);
          }
        }
      }

      // Criar tipos de subtarefas
      const subtaskTypeErrors: string[] = [];
      if (importWorkflowData.subtaskTypes && importWorkflowData.subtaskTypes.length > 0) {
        // Buscar todos os workflows para mapear nomes para IDs
        const allWorkflowsResponse = await api.get(`/api/workflows`);
        const allWorkflows = allWorkflowsResponse.data || [];

        for (const subtaskTypeData of importWorkflowData.subtaskTypes) {
          try {
            let workflowIdForSubtask = null;
            
            // Se há um nome de workflow para subtarefa, buscar o ID correspondente
            if (subtaskTypeData.workflow_name_for_subtask) {
              const targetWorkflow = allWorkflows.find(
                (w: Workflow) => w.name === subtaskTypeData.workflow_name_for_subtask
              );
              if (targetWorkflow) {
                workflowIdForSubtask = targetWorkflow.id;
              } else {
                console.warn(`Workflow "${subtaskTypeData.workflow_name_for_subtask}" não encontrado para tipo de subtarefa "${subtaskTypeData.name}"`);
                // Continuar sem o workflow_id_for_subtask
              }
            }

            await api.post(`/api/workflows/${newWorkflowId}/subtask-types`, {
              name: subtaskTypeData.name,
              description: subtaskTypeData.description || null,
              workflow_id_for_subtask: workflowIdForSubtask,
              default_priority: subtaskTypeData.default_priority || 'p3',
              order: subtaskTypeData.order || 0,
              is_active: subtaskTypeData.is_active !== false
            });
          } catch (error: any) {
            console.error(`Erro ao criar tipo de subtarefa "${subtaskTypeData.name}":`, error);
            let errorMessage = '';
            if (error.response?.data) {
              if (typeof error.response.data.detail === 'string') {
                errorMessage = error.response.data.detail;
              } else if (error.response.data.detail?.message) {
                errorMessage = error.response.data.detail.message;
              } else if (error.response.data.message) {
                errorMessage = error.response.data.message;
              } else {
                errorMessage = JSON.stringify(error.response.data);
              }
            } else if (error.message) {
              errorMessage = error.message;
            } else {
              errorMessage = 'Erro desconhecido';
            }
            subtaskTypeErrors.push(`Tipo de subtarefa "${subtaskTypeData.name}": ${errorMessage}`);
          }
        }
      }

      // Criar configurações de e-mail
      const emailConfigErrors: string[] = [];
      if (importWorkflowData.emailConfigs && importWorkflowData.emailConfigs.length > 0) {
        for (const emailConfigData of importWorkflowData.emailConfigs) {
          try {
            await api.post(`/api/workflows/${newWorkflowId}/email-configs`, {
              workflow_id: newWorkflowId,
              step_name: emailConfigData.step_name || null,
              trigger_event: emailConfigData.trigger_event,
              recipient_type: emailConfigData.recipient_type,
              enabled: emailConfigData.enabled !== false,
              subject_template: emailConfigData.subject_template || null,
              body_template: emailConfigData.body_template || null
            });
          } catch (error: any) {
            const errorMsg = typeof error.response?.data?.detail === 'string'
              ? error.response.data.detail
              : (error.response?.data?.detail?.message || error.message || 'Erro desconhecido');
            const stepLabel = emailConfigData.step_name ? `etapa "${emailConfigData.step_name}"` : 'escopo global';
            emailConfigErrors.push(
              `Configuração de email (${stepLabel}, evento "${emailConfigData.trigger_event}", destino "${emailConfigData.recipient_type}"): ${errorMsg}`
            );
          }
        }
      }

      // Criar campos customizados
      const fieldErrors: string[] = [];
      const createdFieldIdByName = new Map<string, number>();
      for (const fieldData of importWorkflowData.customFields) {
        try {
          const fieldResponse = await api.post(`/api/workflows/${newWorkflowId}/custom-fields`, {
            workflow_id: newWorkflowId,
            name: fieldData.name,
            label: fieldData.label,
            description: fieldData.description || null,
            field_type: fieldData.field_type,
            options: fieldData.options || null,
            allow_multiple: fieldData.allow_multiple,
            is_required: fieldData.is_required,
            order: fieldData.order,
            group_name: fieldData.group_name || null,
            conditional_operator: fieldData.conditional_operator || null,
            conditional_value: fieldData.conditional_value || null,
            validation_rules: fieldData.validation_rules || null,
            checkbox_label: fieldData.checkbox_label || null,
            checkbox_text: fieldData.checkbox_text || null,
            checkbox_text_trigger_option: fieldData.checkbox_text_trigger_option || null,
            checkbox_link: fieldData.checkbox_link || null,
            checkbox_link_text: fieldData.checkbox_link_text || null,
            document_type_id: fieldData.document_type_id || null
          });

          const fieldId = fieldResponse.data.id;
          createdFieldIdByName.set(fieldData.name, fieldId);

          // Criar configurações de step para este campo
          const fieldStepConfigs = importWorkflowData.stepConfigs.filter(
            (config: any) => config.fieldName === fieldData.name
          );

          for (const configData of fieldStepConfigs) {
            try {
              await api.post(`/api/workflows/${newWorkflowId}/custom-fields/${fieldId}/step-configs`, {
                workflow_id: newWorkflowId,
                custom_field_id: fieldId,
                step_name: configData.step_name,
                is_visible: configData.is_visible,
                is_required: configData.is_required,
                is_blocked: configData.is_blocked || false,
                order: configData.order
              });
            } catch (error: any) {
              const errorMsg = typeof error.response?.data?.detail === 'string' 
                ? error.response.data.detail 
                : (error.response?.data?.detail?.message || error.message || 'Erro desconhecido');
              console.warn(`Configuração de step para campo "${fieldData.name}" não pôde ser criada:`, errorMsg);
            }
          }
        } catch (error: any) {
          console.error(`Erro ao criar campo customizado "${fieldData.name}":`, error);
          const friendly = buildFriendlyApiError(error, {
            defaultMessage: 'Falha ao criar campo customizado.',
            endpointHints: ['/custom-fields'],
          });
          fieldErrors.push(`Campo "${fieldData.label || fieldData.name}": ${friendly.details}`);
        }
      }

      // Resolver relações entre campos (condicional e validação) após todos os campos serem criados
      for (const fieldData of importWorkflowData.customFields) {
        const createdFieldId = createdFieldIdByName.get(fieldData.name);
        if (!createdFieldId) continue;

        const updatePayload: any = {};
        const conditionalFieldName = (fieldData.conditional_field_name || '').trim();
        const validationFieldName = (fieldData.validation_field_name || '').trim();

        if (conditionalFieldName) {
          const conditionalFieldId = createdFieldIdByName.get(conditionalFieldName);
          if (conditionalFieldId) {
            updatePayload.conditional_field_id = conditionalFieldId;
          } else {
            fieldErrors.push(
              `Campo "${fieldData.label || fieldData.name}": campo condicional "${conditionalFieldName}" não encontrado no arquivo importado`
            );
          }
        }

        if (validationFieldName) {
          const validationFieldId = createdFieldIdByName.get(validationFieldName);
          if (validationFieldId) {
            updatePayload.validation_field_id = validationFieldId;
          } else {
            fieldErrors.push(
              `Campo "${fieldData.label || fieldData.name}": campo de validação "${validationFieldName}" não encontrado no arquivo importado`
            );
          }
        }

        if (Object.keys(updatePayload).length > 0) {
          try {
            await api.put(`/api/workflows/${newWorkflowId}/custom-fields/${createdFieldId}`, updatePayload);
          } catch (error: any) {
            const errorMsg = typeof error.response?.data?.detail === 'string'
              ? error.response.data.detail
              : (error.response?.data?.detail?.message || error.message || 'Erro desconhecido');
            fieldErrors.push(`Campo "${fieldData.label || fieldData.name}": erro ao vincular regras (${errorMsg})`);
          }
        }
      }

      // Mostrar resultado da importação
      const allErrors = [...subtaskTypeErrors, ...emailConfigErrors, ...fieldErrors];
      if (allErrors.length > 0) {
        const errorTypes = [];
        if (subtaskTypeErrors.length > 0) errorTypes.push(`${subtaskTypeErrors.length} tipo(s) de subtarefa`);
        if (emailConfigErrors.length > 0) errorTypes.push(`${emailConfigErrors.length} configuração(ões) de email`);
        if (fieldErrors.length > 0) errorTypes.push(`${fieldErrors.length} campo(s) customizado(s)`);
        
        setAlertModalData({
          title: 'Importação Parcial',
          message: `O fluxo foi criado com sucesso, mas ${errorTypes.join(' e ')} não puderam ser criados.`,
          variant: 'warning',
          details: allErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')
        });
      } else {
        setAlertModalData({
          title: 'Sucesso',
          message: 'Fluxo importado com sucesso!',
          variant: 'success'
        });
      }
      setShowAlertModal(true);
      setShowImportModal(false);
      setImportWorkflowName('');
      setImportWorkflowDescription('');
      setImportWorkflowData(null);
      fetchWorkflows();
    } catch (error: any) {
      console.error('Erro ao importar fluxo:', error);
      setAlertModalData({
        title: 'Erro na Importação',
        message: error.response?.data?.detail || error.message || 'Erro desconhecido ao importar fluxo.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'analista') {
    return <div>Acesso negado</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {currentArea === 'purchases'
            ? 'Fluxos de Requisições'
            : currentArea === 'changes'
              ? 'Fluxos de Mudanças'
              : 'Fluxos de Incidentes'}
        </h2>
        {(currentUser?.role === 'admin' || currentUser?.role === 'analista') && (
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => setShowImportModal(true)}>
              Importar Fluxo
            </Button>
          <Button variant="success" onClick={() => setShowModal(true)}>
            Novo Fluxo
          </Button>
          </div>
        )}
      </div>

      <Card>
        <Card.Body>
          {workflows.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum fluxo encontrado</p>
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <Form.Group style={{ minWidth: '260px', flex: '1 1 260px' }}>
                  <Form.Label>Buscar</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nome ou descrição; para ID use #número"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>

                <Form.Group style={{ minWidth: '180px' }}>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  >
                    <option value="all">Todos</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group style={{ minWidth: '180px' }}>
                  <Form.Label>Publicação</Form.Label>
                  <Form.Select
                    value={publicationFilter}
                    onChange={(e) => setPublicationFilter(e.target.value as 'all' | 'published' | 'draft')}
                  >
                    <option value="all">Todos</option>
                    <option value="published">Publicados</option>
                    <option value="draft">Rascunhos</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group style={{ minWidth: '180px' }}>
                  <Form.Label>Ordenar por</Form.Label>
                  <Form.Select
                    value={sortField}
                    onChange={(e) =>
                      setSortField(
                        e.target.value as 'id' | 'name' | 'version' | 'status' | 'publication' | 'created_at'
                      )
                    }
                  >
                    <option value="created_at">Data de criação</option>
                    <option value="name">Nome</option>
                    <option value="id">ID</option>
                    <option value="version">Versão</option>
                    <option value="status">Status</option>
                    <option value="publication">Publicação</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group style={{ minWidth: '140px' }}>
                  <Form.Label>Direção</Form.Label>
                  <Form.Select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                  >
                    <option value="desc">Decrescente</option>
                    <option value="asc">Crescente</option>
                  </Form.Select>
                </Form.Group>

                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setPublicationFilter('all');
                    setSortField('created_at');
                    setSortDirection('desc');
                  }}
                >
                  Limpar
                </Button>
              </div>

              <div className="text-muted small mb-2">
                Exibindo {filteredAndSortedWorkflows.length} de {workflows.length} fluxo(s).
              </div>

              {filteredAndSortedWorkflows.length === 0 ? (
                <div className="empty-state">
                  <p>Nenhum fluxo encontrado com os filtros atuais.</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Versão</th>
                      <th>Descrição</th>
                      <th>Status</th>
                      <th>Publicação</th>
                      <th>Data de Criação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedWorkflows.map((workflow: Workflow) => (
                      <tr key={workflow.id}>
                        <td>{workflow.id}</td>
                        <td>{workflow.name}</td>
                        <td>
                          <Badge bg="info">v{workflow.version || 1}</Badge>
                        </td>
                        <td>{workflow.description || '-'}</td>
                        <td>
                          <Badge bg={workflow.is_active ? 'success' : 'secondary'}>
                            {workflow.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td>
                          {workflow.is_published ? (
                            <Badge bg="primary">Publicado</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">Rascunho</Badge>
                          )}
                        </td>
                        <td>{new Date(workflow.created_at).toLocaleDateString('pt-BR')}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                        {/* Ações de Visualização */}
                        <ActionIconButton
                          variant="outline-info"
                          icon={<span aria-hidden="true">👁</span>}
                          label="Visualizar fluxo"
                          onClick={() => {
                            setEditingWorkflow(workflow);
                            setShowViewModal(true);
                          }}
                        />
                        <ActionIconButton
                          variant="outline-secondary"
                          icon={<span aria-hidden="true">⬇</span>}
                          label="Exportar fluxo para JSON"
                          onClick={() => handleExport(workflow)}
                        />
                        <ActionIconButton
                          variant="outline-primary"
                          icon={<span aria-hidden="true">🕘</span>}
                          label="Ver versões do fluxo"
                          onClick={() => handleViewVersions(workflow)}
                        />
                        
                      {(currentUser?.role === 'admin' || currentUser?.role === 'analista') && (
                        <>
                            {/* Ações de Edição e Configuração */}
                            <ActionIconButton
                              variant="outline-primary"
                              icon={<span aria-hidden="true">✎</span>}
                              label="Editar fluxo"
                              onClick={() => {
                                setEditingWorkflow(workflow);
                                setShowModal(true);
                              }}
                              disabled={workflow.is_published}
                            />
                            {!workflow.is_published && (
                              <ActionIconButton
                                variant="outline-success"
                                icon={<span aria-hidden="true">⬆</span>}
                                label="Publicar fluxo"
                                onClick={() => handlePublish(workflow)}
                              />
                            )}
                            <ActionIconButton
                              variant="outline-warning"
                              icon={<span aria-hidden="true">➕</span>}
                              label="Criar nova versão"
                              onClick={() => handleNewVersion(workflow)}
                            />
                            <ActionIconButton
                              variant="outline-success"
                              icon={<span aria-hidden="true">👥</span>}
                              label="Gerenciar grupos"
                              onClick={() => {
                                setEditingWorkflow(workflow);
                                setShowGroupsModal(true);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-warning"
                              icon={<span aria-hidden="true">☑</span>}
                              label="Gerenciar campos customizados"
                              onClick={() => {
                                setEditingWorkflow(workflow);
                                setShowCustomFieldsModal(true);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-secondary"
                              icon={<span aria-hidden="true">☰</span>}
                              label="Gerenciar tipos de subtarefas"
                              onClick={() => {
                                setEditingWorkflow(workflow);
                                setShowSubtaskTypesModal(true);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-info"
                              icon={<span aria-hidden="true">✉</span>}
                              label="Configurar notificações por email"
                              onClick={() => {
                                setEditingWorkflow(workflow);
                                setShowEmailConfigModal(true);
                              }}
                            />
                            
                            {/* Separador visual */}
                            <span className="mx-1 text-muted">|</span>
                            
                            {/* Ações de Status */}
                            <ActionIconButton
                              variant={workflow.is_active ? 'outline-danger' : 'outline-success'}
                              icon={<span aria-hidden="true">{workflow.is_active ? '◯' : '⬤'}</span>}
                              label={workflow.is_active ? 'Desativar fluxo' : 'Ativar fluxo'}
                              onClick={() => handleToggleWorkflow(workflow)}
                            />
                            
                            {/* Ações Destrutivas */}
                            <ActionIconButton
                              variant="outline-danger"
                              icon={<span aria-hidden="true">🗑</span>}
                              label="Excluir definitivamente"
                              onClick={() => handleDeleteWorkflow(workflow)}
                            />
                        </>
                      )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {(currentUser?.role === 'admin' || currentUser?.role === 'analista') && (
        <WorkflowModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditingWorkflow(null);
          }}
          workflow={editingWorkflow}
          onSuccess={() => {
            fetchWorkflows();
          }}
        />
      )}

      <Modal 
        show={showViewModal} 
        onHide={() => {
          setShowViewModal(false);
          setEditingWorkflow(null);
          setIsViewModalMaximized(false);
        }} 
        size={isViewModalMaximized ? undefined : "xl"} 
        fullscreen={isViewModalMaximized ? true : "lg-down"}
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100 me-3">
            <Modal.Title className="mb-0">Visualizar Fluxo: {editingWorkflow?.name}</Modal.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setIsViewModalMaximized(!isViewModalMaximized)}
              title={isViewModalMaximized ? "Restaurar" : "Maximizar"}
            >
              {isViewModalMaximized ? (
                <span aria-hidden="true">🗗</span>
              ) : (
                <span aria-hidden="true">⛶</span>
              )}
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, overflow: 'hidden' }}>
          {editingWorkflow && (
            <div style={{ height: isViewModalMaximized ? 'calc(100vh - 120px)' : '70vh', minHeight: '500px' }}>
              <WorkflowBPMNViewer workflow={editingWorkflow} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowViewModal(false);
            setEditingWorkflow(null);
            setIsViewModalMaximized(false);
          }}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {(currentUser?.role === 'admin' || currentUser?.role === 'analista') && editingWorkflow && (
        <>
          <WorkflowGroupsModal
            show={showGroupsModal}
            onHide={() => {
              setShowGroupsModal(false);
              setEditingWorkflow(null);
            }}
            workflow={editingWorkflow}
            onSuccess={fetchWorkflows}
          />
          <CustomFieldsManager
            show={showCustomFieldsModal}
            onHide={() => {
              setShowCustomFieldsModal(false);
              setEditingWorkflow(null);
            }}
            workflowId={editingWorkflow.id}
            workflowName={editingWorkflow.name}
            workflowVersion={editingWorkflow.version}
            workflowParentId={editingWorkflow.parent_workflow_id}
            workflowIsPublished={editingWorkflow.is_published || false}
            workflowAreaType={
              editingWorkflow.area_type === 'purchases' || currentArea === 'purchases'
                ? 'purchases'
                : editingWorkflow.area_type === 'changes' || currentArea === 'changes'
                  ? 'changes'
                : 'tickets'
            }
            workflowSteps={(() => {
              if (!editingWorkflow.steps) return [];
              try {
                const stepsData = JSON.parse(editingWorkflow.steps);
                if (Array.isArray(stepsData)) {
                  return stepsData.map((s: any) => ({
                    name: typeof s === 'string' ? s : (s.name || s),
                  }));
                } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
                  return stepsData.steps.map((s: any) => ({
                    name: typeof s === 'string' ? s : (s.name || s),
                  }));
                }
              } catch (e) {
                return [];
              }
              return [];
            })()}
          />
          <SubtaskTypesManager
            show={showSubtaskTypesModal}
            onHide={() => {
              setShowSubtaskTypesModal(false);
              setEditingWorkflow(null);
            }}
            workflow={editingWorkflow}
          />
          <WorkflowEmailConfigModal
            show={showEmailConfigModal}
            onHide={() => {
              setShowEmailConfigModal(false);
              setEditingWorkflow(null);
            }}
            workflow={editingWorkflow}
          />
        </>
      )}

      {/* Modal de Importação */}
      <Modal show={showImportModal} onHide={() => {
        setShowImportModal(false);
        setImportWorkflowName('');
        setImportWorkflowDescription('');
        setImportWorkflowData(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Importar Fluxo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Selecione um arquivo JSON exportado para importar um novo fluxo. Você pode editar o nome e descrição antes de importar.</p>
          
          <Form.Group className="mb-3">
            <Form.Label>Arquivo JSON</Form.Label>
            <Form.Control
              type="file"
              accept=".json"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileSelect(file);
                }
              }}
            />
            <Form.Text className="text-muted">
              O arquivo deve ser um JSON exportado anteriormente do sistema (schema_version 1).
            </Form.Text>
          </Form.Group>

          {importWorkflowData && (
            <>
              <hr />
              <h6>Configurações do Fluxo</h6>
              <Form.Group className="mb-3">
                <Form.Label>Nome do Fluxo *</Form.Label>
                <Form.Control
                  type="text"
                  value={importWorkflowName}
                  onChange={(e) => setImportWorkflowName(e.target.value)}
                  placeholder="Nome do fluxo"
                />
                <Form.Text className="text-muted">
                  Você pode alterar o nome do fluxo antes de importar.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={importWorkflowDescription}
                  onChange={(e) => setImportWorkflowDescription(e.target.value)}
                  placeholder="Descrição do fluxo"
                />
              </Form.Group>

              <div className="mb-3">
                <small className="text-muted">
                  <strong>Resumo:</strong> {importWorkflowData.steps.length} etapa(s), {importWorkflowData.groups.length} grupo(s), {importWorkflowData.customFields.length} campo(s) customizado(s), {importWorkflowData.subtaskTypes?.length || 0} tipo(s) de subtarefa(s)
                </small>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowImportModal(false);
              setImportWorkflowName('');
              setImportWorkflowDescription('');
              setImportWorkflowData(null);
            }}
          >
            Cancelar
          </Button>
          {importWorkflowData && (
            <Button 
              variant="success" 
              onClick={handleImportConfirm}
              disabled={!importWorkflowName.trim()}
            >
              Importar Fluxo
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModalData.title}
        message={alertModalData.message}
        variant={alertModalData.variant}
        details={alertModalData.details}
      />

      {/* Modal de Confirmação para Ativar/Desativar */}
      <Modal show={showConfirmModal} onHide={() => {
        setShowConfirmModal(false);
        setWorkflowToToggle(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Ação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflowToToggle && (
            <p>
              Tem certeza que deseja {workflowToToggle.is_active ? 'desativar' : 'ativar'} o fluxo 
              <strong> "{workflowToToggle.name}"</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowConfirmModal(false);
            setWorkflowToToggle(null);
          }}>
            Cancelar
          </Button>
          <Button 
            variant={workflowToToggle?.is_active ? "danger" : "success"} 
            onClick={handleConfirmToggle}
          >
            {workflowToToggle?.is_active ? 'Desativar' : 'Ativar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação para Exclusão Definitiva */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
        setWorkflowToDelete(null);
      }} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>⚠️ Excluir Definitivamente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflowToDelete && (
            <>
              <div className="alert alert-danger">
                <strong>Atenção!</strong> Esta ação não pode ser desfeita.
              </div>
              <p>
                Tem certeza que deseja excluir <strong>definitivamente</strong> o fluxo 
                <strong> "{workflowToDelete.name}"</strong>?
              </p>
              <p className="text-muted small">
                Todos os dados relacionados (campos customizados, configurações de step, associações com grupos) serão permanentemente removidos.
                <br />
                <strong>Nota:</strong> Não é possível excluir um fluxo que possui tickets associados.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setWorkflowToDelete(null);
          }}>
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
          >
            Excluir Definitivamente
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação para Publicar */}
      <Modal show={showPublishConfirmModal} onHide={() => {
        setShowPublishConfirmModal(false);
        setWorkflowToPublish(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Publicar Fluxo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflowToPublish && (
            <>
              <p>
                Deseja publicar o fluxo <strong>"{workflowToPublish.name}"</strong> versão <strong>v{workflowToPublish.version || 1}</strong>?
              </p>
              <div className="alert alert-warning">
                <strong>Importante:</strong> Ao publicar, todas as versões anteriores publicadas do mesmo fluxo serão desativadas automaticamente.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPublishConfirmModal(false);
            setWorkflowToPublish(null);
          }}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmPublish}>
            Publicar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação para Nova Versão */}
      <Modal show={showNewVersionConfirmModal} onHide={() => {
        setShowNewVersionConfirmModal(false);
        setWorkflowToNewVersion(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Criar Nova Versão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflowToNewVersion && (
            <>
              <p>
                Deseja criar uma nova versão do fluxo <strong>"{workflowToNewVersion.name}"</strong>?
              </p>
              <div className="alert alert-info">
                <strong>Informação:</strong> A nova versão será criada como rascunho e poderá ser editada livremente. A versão atual não será afetada.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowNewVersionConfirmModal(false);
            setWorkflowToNewVersion(null);
          }}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmNewVersion}>
            Criar Nova Versão
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Versões do Fluxo */}
      <Modal show={showVersionsModal} onHide={() => {
        setShowVersionsModal(false);
        setEditingWorkflow(null);
        setWorkflowVersions([]);
      }} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Versões do Fluxo: {editingWorkflow?.name || 'Carregando...'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingVersions ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : workflowVersions.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">Nenhuma versão encontrada.</p>
              </div>
            ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Versão</th>
                  <th>Status</th>
                  <th>Publicação</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {workflowVersions.map((version: Workflow) => (
                  <tr key={version.id}>
                    <td>{version.id}</td>
                    <td>
                      <Badge bg="info">v{version.version || 1}</Badge>
                    </td>
                    <td>
                      <Badge bg={version.is_active ? 'success' : 'secondary'}>
                        {version.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>
                      {version.is_published ? (
                        <Badge bg="primary">Publicado</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">Rascunho</Badge>
                      )}
                    </td>
                    <td>{new Date(version.created_at).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <ActionIconButton
                          variant="outline-info"
                          icon={<span aria-hidden="true">👁</span>}
                          label="Visualizar fluxo"
                          onClick={() => {
                            setEditingWorkflow(version);
                            setShowViewModal(true);
                            setShowVersionsModal(false);
                          }}
                        />
                        {(currentUser?.role === 'admin' || currentUser?.role === 'analista') && (
                          <>
                            <ActionIconButton
                              variant="outline-primary"
                              icon={<span aria-hidden="true">✎</span>}
                              label="Editar fluxo"
                              onClick={() => {
                                setEditingWorkflow(version);
                                setShowModal(true);
                                setShowVersionsModal(false);
                              }}
                              disabled={version.is_published}
                            />
                            {!version.is_published && (
                              <ActionIconButton
                                variant="outline-success"
                                icon={<span aria-hidden="true">⬆</span>}
                                label="Publicar fluxo"
                                onClick={() => {
                                  setWorkflowToPublish(version);
                                  setShowPublishConfirmModal(true);
                                  setShowVersionsModal(false);
                                }}
                              />
                            )}
                            <ActionIconButton
                              variant="outline-warning"
                              icon={<span aria-hidden="true">➕</span>}
                              label="Criar nova versão"
                              onClick={() => {
                                setWorkflowToNewVersion(version);
                                setShowNewVersionConfirmModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-success"
                              icon={<span aria-hidden="true">👥</span>}
                              label="Gerenciar grupos"
                              onClick={() => {
                                setEditingWorkflow(version);
                                setShowGroupsModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-warning"
                              icon={<span aria-hidden="true">☑</span>}
                              label="Gerenciar campos customizados"
                              onClick={() => {
                                setEditingWorkflow(version);
                                setShowCustomFieldsModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-secondary"
                              icon={<span aria-hidden="true">☰</span>}
                              label="Gerenciar tipos de subtarefas"
                              onClick={() => {
                                setEditingWorkflow(version);
                                setShowSubtaskTypesModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-info"
                              icon={<span aria-hidden="true">✉</span>}
                              label="Configurar notificações por email"
                              onClick={() => {
                                setEditingWorkflow(version);
                                setShowEmailConfigModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant={version.is_active ? 'outline-danger' : 'outline-success'}
                              icon={<span aria-hidden="true">{version.is_active ? '◯' : '⬤'}</span>}
                              label={version.is_active ? 'Desativar fluxo' : 'Ativar fluxo'}
                              onClick={() => {
                                setWorkflowToToggle({
                                  id: version.id,
                                  name: version.name,
                                  is_active: version.is_active
                                });
                                setShowConfirmModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                            <ActionIconButton
                              variant="outline-danger"
                              icon={<span aria-hidden="true">🗑</span>}
                              label="Excluir definitivamente"
                              onClick={() => {
                                setWorkflowToDelete({
                                  id: version.id,
                                  name: version.name
                                });
                                setShowDeleteModal(true);
                                setShowVersionsModal(false);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowVersionsModal(false);
            setEditingWorkflow(null);
            setWorkflowVersions([]);
          }}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

interface WorkflowStep {
  name: string;
  status: string;
  description?: string;
  previous_steps?: string[];
  allow_anytime_access?: boolean;
}

const WorkflowModal: React.FC<{
  show: boolean;
  onHide: () => void;
  workflow: Workflow | null;
  onSuccess: () => void;
}> = ({ show, onHide, workflow, onSuccess }: {
  show: boolean;
  onHide: () => void;
  workflow: Workflow | null;
  onSuccess: () => void;
}) => {
  const { currentArea } = useArea();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState<{title: string; message: string; variant: 'success' | 'danger' | 'warning' | 'info'}>({
    title: '',
    message: '',
    variant: 'info'
  });

  React.useEffect(() => {
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description || '');
      
      // Parse steps from JSON
      let steps: WorkflowStep[] = [];
      if (workflow.steps) {
        try {
          const stepsData = JSON.parse(workflow.steps);
          if (Array.isArray(stepsData)) {
            steps = stepsData.map((s: any) => 
              typeof s === 'string' 
                ? { name: s, status: 'em_andamento' }
                : { 
                    name: s.name || s, 
                    status: s.status || 'em_andamento', 
                    description: s.description,
                    previous_steps: s.previous_steps || undefined,
                    allow_anytime_access: !!s.allow_anytime_access,
                  }
            );
          } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
            steps = stepsData.steps.map((s: any) => 
              typeof s === 'string' 
                ? { name: s, status: 'em_andamento' }
                : { 
                    name: s.name || s, 
                    status: s.status || 'em_andamento', 
                    description: s.description,
                    previous_steps: s.previous_steps || undefined,
                    allow_anytime_access: !!s.allow_anytime_access,
                  }
            );
          }
        } catch (e) {
          // Se não for JSON válido, tratar como string simples
          steps = workflow.steps.split(',').map((s: string) => ({ 
            name: s.trim(), 
            status: 'em_andamento' 
          }));
        }
      }
      setWorkflowSteps(steps);
    } else {
      // Resetar formulário quando não há workflow (novo fluxo ou modal fechado)
      setName('');
      setDescription('');
      setWorkflowSteps([]);
    }
  }, [workflow, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação manual
    if (!name.trim()) {
      setAlertModalData({
        title: 'Atenção',
        message: 'Por favor, informe o nome do fluxo.',
        variant: 'warning'
      });
      setShowAlertModal(true);
      return;
    }

    if (workflowSteps.length === 0) {
      setAlertModalData({
        title: 'Atenção',
        message: 'Por favor, adicione pelo menos uma etapa ao fluxo.',
        variant: 'warning'
      });
      setShowAlertModal(true);
      return;
    }

    setLoading(true);

    try {
      // Converter steps para JSON
      const stepsJson = JSON.stringify(workflowSteps);

      if (workflow) {
        // Verificar se o workflow está publicado
        if (workflow.is_published) {
          setAlertModalData({
            title: 'Atenção',
            message: 'Não é possível editar um fluxo publicado. Crie uma nova versão para fazer alterações.',
            variant: 'warning'
          });
          setShowAlertModal(true);
          setLoading(false);
          return;
        }

        await api.put(`/api/workflows/${workflow.id}`, {
          name,
          description,
          steps: stepsJson,
          area_type: currentArea, // Incluir area_type na edição também
        });
      } else {
        await api.post(`/api/workflows`, {
          name,
          description,
          steps: stepsJson,
          area_type: currentArea, // Incluir area_type na criação
        });
      }
      
      // Mostrar mensagem de sucesso
      setAlertModalData({
        title: 'Sucesso',
        message: workflow ? `Fluxo "${name}" atualizado com sucesso!` : `Fluxo "${name}" criado com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
      
      // Atualizar lista e fechar modal após um pequeno delay para mostrar a mensagem
      onSuccess();
      setTimeout(() => {
        onHide();
        // Resetar formulário quando fechar
        setName('');
        setDescription('');
        setWorkflowSteps([]);
      }, 500);
    } catch (error: any) {
      console.error('Erro ao salvar fluxo:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao salvar fluxo';
      setAlertModalData({
        title: 'Erro',
        message: errorMessage,
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{workflow ? 'Editar Fluxo' : 'Novo Fluxo'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            />
          </Form.Group>

          <WorkflowBuilder
            steps={workflowSteps}
            onChange={setWorkflowSteps}
            workflowAreaType={currentArea === 'purchases' ? 'purchases' : currentArea === 'changes' ? 'changes' : 'tickets'}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading || workflowSteps.length === 0}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModalData.title}
        message={alertModalData.message}
        variant={alertModalData.variant}
      />
    </Modal>
  );
};

const WorkflowGroupsModal: React.FC<{
  show: boolean;
  onHide: () => void;
  workflow: Workflow;
  onSuccess: () => void;
}> = ({ show, onHide, workflow, onSuccess }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [workflowGroups, setWorkflowGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState<{title: string; message: string; variant: 'success' | 'danger' | 'warning' | 'info'}>({
    title: '',
    message: '',
    variant: 'info'
  });
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState<{id: number; name: string} | null>(null);

  const fetchGroups = async () => {
    try {
      const response = await api.get(`/api/groups`, {
        params: { limit: 1000 },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchWorkflowGroups = useCallback(async () => {
    if (!workflow) return;
    try {
      const response = await api.get(`/api/workflows/${workflow.id}/groups`);
      // Extrair grupos da resposta
      const groupsData = response.data.map((wg: any) => wg.group || {
        id: wg.group_id,
        name: wg.group?.name || '',
        description: wg.group?.description || '',
        is_active: wg.group?.is_active || true
      });
      setWorkflowGroups(groupsData);
    } catch (error) {
      console.error('Erro ao carregar grupos do workflow:', error);
    }
  }, [workflow]);

  useEffect(() => {
    if (show && workflow) {
      fetchGroups();
      fetchWorkflowGroups();
    }
  }, [show, workflow, fetchWorkflowGroups]);

  const handleAddGroup = async () => {
    if (!selectedGroupId) return;

    setLoading(true);
    try {
      await api.post(`/api/workflows/${workflow.id}/groups`, {
        group_id: selectedGroupId,
      });
      await fetchWorkflowGroups();
      setSelectedGroupId('');
      onSuccess();
      setAlertModalData({
        title: 'Sucesso',
        message: 'Grupo adicionado ao fluxo com sucesso!',
        variant: 'success'
      });
      setShowAlertModal(true);
    } catch (error: any) {
      console.error('Erro ao adicionar grupo:', error);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao adicionar grupo',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroup = (groupId: number) => {
    const group = workflowGroups.find(g => g.id === groupId);
    if (group) {
      setGroupToRemove({ id: groupId, name: group.name });
      setShowRemoveConfirmModal(true);
    }
  };

  const handleConfirmRemoveGroup = async () => {
    if (!groupToRemove) return;

    setLoading(true);
    try {
      await api.delete(`/api/workflows/${workflow.id}/groups/${groupToRemove.id}`);
      await fetchWorkflowGroups();
      onSuccess();
      setShowRemoveConfirmModal(false);
      setGroupToRemove(null);
      setAlertModalData({
        title: 'Sucesso',
        message: `Grupo "${groupToRemove.name}" removido do fluxo com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
    } catch (error: any) {
      console.error('Erro ao remover grupo:', error);
      setShowRemoveConfirmModal(false);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao remover grupo',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setGroupToRemove(null);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar grupos que ainda não estão associados
  const availableGroups = groups.filter(
    (group) => !workflowGroups.some((wg) => wg.id === group.id) && group.is_active
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Grupos que Atendem ao Processo: {workflow.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching ? (
          <div>Carregando...</div>
        ) : (
          <>
            {workflow.is_published && (
              <Alert variant="warning" className="mb-3">
                <Alert.Heading>Atenção</Alert.Heading>
                <p>Este fluxo está publicado. Para fazer alterações, crie uma nova versão.</p>
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Adicionar Grupo</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value ? Number(e.target.value) : '')}
                  disabled={loading || availableGroups.length === 0 || workflow.is_published}
                >
                  <option value="">Selecione um grupo</option>
                  {availableGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="success"
                  onClick={handleAddGroup}
                  disabled={loading || !selectedGroupId || availableGroups.length === 0 || workflow.is_published}
                >
                  Adicionar
                </Button>
              </div>
            </Form.Group>

            <div className="mb-3">
              <h6>Grupos Associados ({workflowGroups.length})</h6>
              {workflowGroups.length === 0 ? (
                <p className="text-muted">Nenhum grupo associado a este processo.</p>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workflowGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.id}</td>
                        <td>{group.name}</td>
                        <td>{group.description || '-'}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveGroup(group.id)}
                            disabled={loading || workflow.is_published}
                            title={workflow.is_published ? "Não é possível remover grupos de um fluxo publicado" : "Remover grupo"}
                          >
                            Remover
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModalData.title}
        message={alertModalData.message}
        variant={alertModalData.variant}
      />

      {/* Modal de Confirmação para Remover Grupo */}
      <Modal show={showRemoveConfirmModal} onHide={() => {
        setShowRemoveConfirmModal(false);
        setGroupToRemove(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remover Grupo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {groupToRemove && (
            <p>
              Tem certeza que deseja remover o grupo <strong>"{groupToRemove.name}"</strong> deste fluxo?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowRemoveConfirmModal(false);
            setGroupToRemove(null);
          }}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmRemoveGroup} disabled={loading}>
            Remover
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

interface WorkflowEmailConfig {
  id: number;
  workflow_id: number;
  step_name?: string;
  trigger_event: 'step_change' | 'info_change' | 'group_assignment' | 'user_assignment' | 'comment_added' | 'status_change';
  recipient_type: 'creator' | 'assignee' | 'group' | 'group_members' | 'group_leaders' | 'current_step_group';
  enabled: boolean;
  subject_template?: string;
  body_template?: string;
  created_at: string;
  updated_at: string;
}

const WorkflowEmailConfigModal: React.FC<{
  show: boolean;
  onHide: () => void;
  workflow: Workflow | null;
}> = ({ show, onHide, workflow }) => {
  const [configs, setConfigs] = useState<WorkflowEmailConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [workflowSteps, setWorkflowSteps] = useState<Array<{ name: string }>>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<WorkflowEmailConfig | null>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState<{title: string; message: string; variant: 'success' | 'danger' | 'warning' | 'info'}>({
    title: '',
    message: '',
    variant: 'info'
  });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<number | null>(null);

  const parseWorkflowSteps = useCallback(() => {
    if (!workflow?.steps) {
      setWorkflowSteps([]);
      return;
    }
    try {
      const stepsData = JSON.parse(workflow.steps);
      let steps: Array<{ name: string }> = [];
      if (Array.isArray(stepsData)) {
        steps = stepsData.map((s: any) => ({
          name: typeof s === 'string' ? s : (s.name || s),
        }));
      } else if (stepsData.steps && Array.isArray(stepsData.steps)) {
        steps = stepsData.steps.map((s: any) => ({
          name: typeof s === 'string' ? s : (s.name || s),
        }));
      }
      setWorkflowSteps(steps);
    } catch (e) {
      setWorkflowSteps([]);
    }
  }, [workflow?.steps]);

  const fetchConfigs = useCallback(async () => {
    if (!workflow) return;
    setFetching(true);
    try {
      const response = await api.get(`/api/workflows/${workflow.id}/email-configs`);
      setConfigs(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações de email:', error);
    } finally {
      setFetching(false);
    }
  }, [workflow]);

  useEffect(() => {
    if (show && workflow) {
      fetchConfigs();
      parseWorkflowSteps();
    }
  }, [show, workflow, fetchConfigs, parseWorkflowSteps]);

  const handleDelete = (configId: number) => {
    setConfigToDelete(configId);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!workflow || !configToDelete) return;

    setLoading(true);
    try {
      await api.delete(`/api/workflows/${workflow.id}/email-configs/${configToDelete}`);
      await fetchConfigs();
      setShowDeleteConfirmModal(false);
      setConfigToDelete(null);
      setAlertModalData({
        title: 'Sucesso',
        message: 'Configuração de email excluída com sucesso!',
        variant: 'success'
      });
      setShowAlertModal(true);
    } catch (error: any) {
      console.error('Erro ao excluir configuração:', error);
      setShowDeleteConfirmModal(false);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao excluir configuração',
        variant: 'danger'
      });
      setShowAlertModal(true);
      setConfigToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (config: WorkflowEmailConfig) => {
    if (!workflow) return;
    setLoading(true);
    try {
      await api.put(`/api/workflows/${workflow.id}/email-configs/${config.id}`, {
        enabled: !config.enabled,
      });
      await fetchConfigs();
      setAlertModalData({
        title: 'Sucesso',
        message: `Configuração ${!config.enabled ? 'ativada' : 'desativada'} com sucesso!`,
        variant: 'success'
      });
      setShowAlertModal(true);
    } catch (error: any) {
      console.error('Erro ao atualizar configuração:', error);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao atualizar configuração',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const getTriggerEventLabel = (event: string) => {
    const labels: Record<string, string> = {
      step_change: 'Mudança de Etapa',
      info_change: 'Mudança de Informações',
      group_assignment: 'Atribuição a Grupo',
      user_assignment: 'Atribuição a Usuário',
      comment_added: 'Novo Comentário',
      status_change: 'Mudança de Status',
    };
    return labels[event] || event;
  };

  const getRecipientTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      creator: 'Criador do Ticket',
      assignee: 'Usuário Atribuído',
      group: 'Email do Grupo',
      group_members: 'Todos os Membros do Grupo',
      group_leaders: 'Líderes do Grupo',
      current_step_group: 'Grupo da Etapa Atual',
    };
    return labels[type] || type;
  };

  if (!workflow) return null;

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Configuração de Emails: {workflow.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fetching ? (
            <div>Carregando...</div>
          ) : (
            <>
              {workflow.is_published && (
                <Alert variant="warning" className="mb-3">
                  <Alert.Heading>Atenção</Alert.Heading>
                  <p>Este fluxo está publicado. Para fazer alterações, crie uma nova versão.</p>
                </Alert>
              )}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>Configurações de Notificação por Email</h6>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowAddModal(true)}
                  disabled={workflow.is_published}
                >
                  Nova Configuração
                </Button>
              </div>

              {configs.length === 0 ? (
                <p className="text-muted">Nenhuma configuração de email definida.</p>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Etapa</th>
                      <th>Evento</th>
                      <th>Destinatário</th>
                      <th>Ativo</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((config) => (
                      <tr key={config.id}>
                        <td>{config.step_name || '(Global)'}</td>
                        <td>{getTriggerEventLabel(config.trigger_event)}</td>
                        <td>{getRecipientTypeLabel(config.recipient_type)}</td>
                        <td>
                          <Badge bg={config.enabled ? 'success' : 'secondary'}>
                            {config.enabled ? 'Sim' : 'Não'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant={config.enabled ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            className="me-1"
                            onClick={() => handleToggleEnabled(config)}
                            disabled={loading || workflow.is_published}
                            title={workflow.is_published ? "Não é possível alterar configurações de email de um fluxo publicado" : (config.enabled ? "Desativar" : "Ativar")}
                          >
                            {config.enabled ? 'Desativar' : 'Ativar'}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => {
                              setEditingConfig(config);
                              setShowAddModal(true);
                            }}
                            disabled={workflow.is_published}
                            title={workflow.is_published ? "Não é possível editar configurações de email de um fluxo publicado" : "Editar configuração"}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config.id)}
                            disabled={loading || workflow.is_published}
                            title={workflow.is_published ? "Não é possível excluir configurações de email de um fluxo publicado" : "Excluir configuração"}
                          >
                            Excluir
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <EmailConfigEditModal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setEditingConfig(null);
        }}
        workflow={workflow}
        workflowSteps={workflowSteps}
        editingConfig={editingConfig}
        onSuccess={fetchConfigs}
      />

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModalData.title}
        message={alertModalData.message}
        variant={alertModalData.variant}
      />

      {/* Modal de Confirmação para Excluir Configuração */}
      <Modal show={showDeleteConfirmModal} onHide={() => {
        setShowDeleteConfirmModal(false);
        setConfigToDelete(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Excluir Configuração</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja excluir esta configuração de email?</p>
          <div className="alert alert-warning">
            <strong>Atenção:</strong> Esta ação não pode ser desfeita.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteConfirmModal(false);
            setConfigToDelete(null);
          }}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={loading}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const EmailConfigEditModal: React.FC<{
  show: boolean;
  onHide: () => void;
  workflow: Workflow;
  workflowSteps: Array<{ name: string }>;
  editingConfig: WorkflowEmailConfig | null;
  onSuccess: () => void;
}> = ({ show, onHide, workflow, workflowSteps, editingConfig, onSuccess }) => {
  const [stepName, setStepName] = useState<string>('');
  const [triggerEvent, setTriggerEvent] = useState<'step_change' | 'info_change' | 'group_assignment' | 'user_assignment' | 'comment_added' | 'status_change'>('step_change');
  const [recipientType, setRecipientType] = useState<'creator' | 'assignee' | 'group' | 'group_members' | 'group_leaders' | 'current_step_group'>('creator');
  const [enabled, setEnabled] = useState(true);
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState<{title: string; message: string; variant: 'success' | 'danger' | 'warning' | 'info'}>({
    title: '',
    message: '',
    variant: 'info'
  });

  useEffect(() => {
    if (editingConfig) {
      setStepName(editingConfig.step_name || '');
      setTriggerEvent(editingConfig.trigger_event);
      setRecipientType(editingConfig.recipient_type);
      setEnabled(editingConfig.enabled);
      setSubjectTemplate(editingConfig.subject_template || '');
      setBodyTemplate(editingConfig.body_template || '');
    } else {
      setStepName('');
      setTriggerEvent('step_change');
      setRecipientType('creator');
      setEnabled(true);
      setSubjectTemplate('');
      setBodyTemplate('');
    }
  }, [editingConfig, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const configData = {
        workflow_id: workflow.id,
        step_name: stepName || null,
        trigger_event: triggerEvent,
        recipient_type: recipientType,
        enabled,
        subject_template: subjectTemplate || null,
        body_template: bodyTemplate || null,
      };

      if (editingConfig) {
        await api.put(`/api/workflows/${workflow.id}/email-configs/${editingConfig.id}`, configData);
      } else {
        await api.post(`/api/workflows/${workflow.id}/email-configs`, configData);
      }
      onSuccess();
      onHide();
      setAlertModalData({
        title: 'Sucesso',
        message: editingConfig ? 'Configuração de email atualizada com sucesso!' : 'Configuração de email criada com sucesso!',
        variant: 'success'
      });
      setShowAlertModal(true);
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      setAlertModalData({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao salvar configuração',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingConfig ? 'Editar Configuração de Email' : 'Nova Configuração de Email'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Etapa (deixe em branco para configuração global)</Form.Label>
            <Form.Select
              value={stepName}
              onChange={(e) => setStepName(e.target.value)}
            >
              <option value="">(Configuração Global)</option>
              {workflowSteps.map((step) => (
                <option key={step.name} value={step.name}>
                  {step.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Evento que Dispara o Email</Form.Label>
            <Form.Select
              value={triggerEvent}
              onChange={(e) => setTriggerEvent(e.target.value as any)}
              required
            >
              <option value="step_change">Mudança de Etapa</option>
              <option value="info_change">Mudança de Informações</option>
              <option value="group_assignment">Atribuição a Grupo</option>
              <option value="user_assignment">Atribuição a Usuário</option>
              <option value="comment_added">Novo Comentário</option>
              <option value="status_change">Mudança de Status</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Destinatário *</Form.Label>
            <Form.Select
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value as any)}
            >
              <option value="creator">Criador do Ticket</option>
              <option value="assignee">Usuário Atribuído</option>
              <option value="group">Email do Grupo</option>
              <option value="group_members">Todos os Membros do Grupo</option>
              <option value="group_leaders">Líderes do Grupo</option>
              <option value="current_step_group">Grupo da Etapa Atual</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Ativo"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Template do Assunto (opcional)</Form.Label>
            <Form.Control
              type="text"
              value={subjectTemplate}
              onChange={(e) => setSubjectTemplate(e.target.value)}
              placeholder="Ex: Ticket #{ticket_id} - {event}"
            />
            <Form.Text className="text-muted">
              Use variáveis: {'{ticket_id}'}, {'{ticket_title}'}, {'{entity_id}'}, {'{entity_title}'}, {'{entity_type}'}, {'{event}'}, {'{user_name}'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Template do Corpo (opcional, HTML)</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
              placeholder="<html>...</html>"
            />
            <Form.Text className="text-muted">
              Use as mesmas variáveis do assunto. Para compras, prefira {'{entity_*}'} para manter o template reutilizável.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>
      </Modal>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModalData.title}
        message={alertModalData.message}
        variant={alertModalData.variant}
      />
    </>
  );
};

export default Workflows;

